import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, ListModel, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { AssignDocumentRequest, CreateDataResponse, DownloadDocumentResponse, initialUnassignedDocumentsValue, UnassignedDocumentRequest, UnassignedDocumentResponse } from "./unassigned-documents-interface";
import { downloadFile } from "shared/utils/common";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const UploadDocument = createAsyncThunk<MainResponse, UnassignedDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/UploadDocument',
    async (request: UnassignedDocumentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + `api/unassigned-documents/upload`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetUnassignedDocuments());
            dispatch(GetUnassignedPrivateDocuments());
            dispatch(showNotification({ message: responseModel.message, type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetUnassignedDocuments = createAsyncThunk<UnassignedDocumentResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/GetUnassignedDocuments',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/get', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UnassignedDocumentResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetUnassignedPrivateDocuments = createAsyncThunk<UnassignedDocumentResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/GetUnassignedPrivateDocuments',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/get/private', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UnassignedDocumentResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadUnassignedDocument = createAsyncThunk<DownloadDocumentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/DownloadDocument',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DownloadDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetUnassignedDocumentBase64 = createAsyncThunk<DownloadDocumentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/GetUnassignedDocumentBase64',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/download?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DownloadDocumentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetUnassignedDocumentTypes = createAsyncThunk<CreateDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/GetUnassignedDocumentTypes',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/types', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreateDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTargetDocumentsList = createAsyncThunk<ListModel<string>[], number, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/GetTargetDocumentsList',
    async (id: number, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/targets?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ListModel<string>[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const AssignUnassignedDocument = createAsyncThunk<DownloadDocumentResponse, AssignDocumentRequest, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/AssignUnassignedDocument',
    async (request: AssignDocumentRequest, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DownloadDocumentResponse;
            dispatch(GetUnassignedDocuments());
            dispatch(GetUnassignedPrivateDocuments());
            dispatch(showNotification({ message: "Document successfully assigned", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteUnassignedDocument = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/DeleteUnassignedDocument',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/delete/' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetUnassignedDocuments());
            dispatch(GetUnassignedPrivateDocuments());
            dispatch(showNotification({ message: responseModel.message, type: NotificationTypes.Success }));

            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ChangeUnassignedDocumentPrivacyType = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/UnassignedDocumentsSlice/ChangeUnassignedDocumentPrivacyType',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/unassigned-documents/change-status/' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(GetUnassignedDocuments());
            dispatch(GetUnassignedPrivateDocuments());
            dispatch(showNotification({ message: responseModel.message, type: NotificationTypes.Success }));

            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UnassignedDocumentsSlice = createSlice({
    name: 'UnassignedDocumentsSlice',
    initialState: initialUnassignedDocumentsValue,
    reducers: {
        resetUnassignedDocumentsSlice: (state) => {
            state.isLoading = false;
            state.unassignedDocuments = [];
            state.unassignedPrivateDocuments = [];
        },
        setUnassignedDocument: (state, action: PayloadAction<string>) => {
            state.unassignedDocument = state.unassignedDocuments.find(doc => doc.id === action.payload) ?? null;
        },
        setUnassignedPrivateDocument: (state, action: PayloadAction<string>) => {
            state.unassignedDocument = state.unassignedPrivateDocuments.find(doc => doc.id === action.payload) ?? null;
        },
        resetUnassignedDocument: (state) => {
            state.unassignedDocument = null;
        },
    },
    extraReducers(builder) {
        //Upload Document
        builder
            .addCase(UploadDocument.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.isLoading = false;
            })
            .addCase(UploadDocument.rejected, (state, action) => {
                state.isLoading = false;
            })
            .addCase(UploadDocument.pending, state => {
                state.isLoading = true;
            })

        //Get Unassigned Documents
        builder
            .addCase(GetUnassignedDocuments.fulfilled, (state, action: PayloadAction<UnassignedDocumentResponse[]>) => {
                state.isLoading = false;
                state.unassignedDocuments = action.payload;
            })
            .addCase(GetUnassignedDocuments.rejected, (state, action) => {
                state.isLoading = false;
            })
            .addCase(GetUnassignedDocuments.pending, state => {
                state.isLoading = true;
            })

        //Get Unassigned Private Documents
        builder
            .addCase(GetUnassignedPrivateDocuments.fulfilled, (state, action: PayloadAction<UnassignedDocumentResponse[]>) => {
                state.isLoading = false;
                state.unassignedPrivateDocuments = action.payload;
            })
            .addCase(GetUnassignedPrivateDocuments.rejected, (state, action) => {
                state.isLoading = false;
            })
            .addCase(GetUnassignedPrivateDocuments.pending, state => {
                state.isLoading = true;
            })

        //Download Document
        builder
            .addCase(DownloadUnassignedDocument.fulfilled, (state, action: PayloadAction<DownloadDocumentResponse>) => {
                downloadFile(action.payload.name, action.payload.content)
            })
            .addCase(DownloadUnassignedDocument.rejected, (state, action) => {
            })
            .addCase(DownloadUnassignedDocument.pending, state => {
            })

        //Get Unassigned Document Base64
        builder
            .addCase(GetUnassignedDocumentBase64.fulfilled, (state, action: PayloadAction<DownloadDocumentResponse>) => {
                if (state.unassignedDocument) {
                    state.unassignedDocument.base64 = action.payload.content;
                }
            })
            .addCase(GetUnassignedDocumentBase64.rejected, (state, action) => {
            })
            .addCase(GetUnassignedDocumentBase64.pending, state => {
            })

        //Get Unassigned Document Types
        builder
            .addCase(GetUnassignedDocumentTypes.fulfilled, (state, action: PayloadAction<CreateDataResponse>) => {
                state.createData = action.payload;
            })
            .addCase(GetUnassignedDocumentTypes.rejected, (state, action) => {
            })
            .addCase(GetUnassignedDocumentTypes.pending, state => {
            })

        //Get Target Documents List
        builder
            .addCase(GetTargetDocumentsList.fulfilled, (state, action: PayloadAction<ListModel<string>[]>) => {
                if (state.createData) {
                    state.createData.targets = action.payload;
                }
            })
            .addCase(GetTargetDocumentsList.rejected, (state, action) => {
            })
            .addCase(GetTargetDocumentsList.pending, state => {
            })

        //Get Target Documents List
        builder
            .addCase(ChangeUnassignedDocumentPrivacyType.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            })
            .addCase(ChangeUnassignedDocumentPrivacyType.rejected, (state, action) => {
            })
            .addCase(ChangeUnassignedDocumentPrivacyType.pending, state => {
            })
    }
});

export const { resetUnassignedDocumentsSlice, setUnassignedDocument, setUnassignedPrivateDocument, resetUnassignedDocument } = UnassignedDocumentsSlice.actions;
export default UnassignedDocumentsSlice.reducer;